import { BasePortletStore, showError } from '../lib';
import { getPOE } from './PoeModel';

const SenseConstants = window['SenseConstants'];

/**
 * Defines the state of an ExtJS framework (modern or classic).
 */
interface ExtJsState {
  loading?: Promise<void>;
  loaded: boolean;
}

/**
 * Constant, that holds the current state of the ExtJS Classic framework.
 */
const stateExtJsClassic: ExtJsState = {
  loading: undefined,
  loaded: false
};

/**
 * Helper method, that adds the version to the path of the resource.
 * @param path the original path.
 * @return the path with the added version.
 */
const enrichWithVersion = function (path: string): string {
  return path + '?version=' + SenseConstants?.version;
};

/**
 * Helper method, that loads a certain CSS and returns a promise for that.
 * @param path the path to the CSS.
 * @return a promise for the CSS loading.
 */
const loadCss = function (path: string): Promise<string> {
  return new Promise<string>(function (resolve, reject) {
    const cssEl = document.createElement('link');
    cssEl.type = 'text/css';
    cssEl.rel = 'stylesheet';
    cssEl.href = path;
    cssEl.addEventListener('load', function () {
      resolve(path);
    });
    cssEl.addEventListener('error', function (e) {
      reject(e);
    });
    document.body.appendChild(cssEl);
  });
};

/**
 * Helper method, that loads a certain Javascript and returns a promise for that.
 * @param path the path to the Javascript.
 * @return a promise for the Javascript loading.
 */
const loadJs = function (path: string): Promise<string> {
  return new Promise<string>(function (resolve, reject) {
    const jsEl = document.createElement('script');
    jsEl.type = 'text/javascript';
    jsEl.src = path;
    jsEl.addEventListener('load', function () {
      resolve(path);
    });
    jsEl.addEventListener('error', function (e) {
      console.error('Error loading js with path ' + path);
      console.error(e);
      reject(e);
    });
    document.body.appendChild(jsEl);
  });
};

/**
 * Public method, that load the ExtJS Classic framework and returns a promise for that.
 * @return the Promise for the ExtJS Classic framework loading. Will always be the same, also for repeated calls to that method.
 */
export const loadExtJsClassic = function (): Promise<void> {
  if (undefined !== stateExtJsClassic.loading) {
    return stateExtJsClassic.loading;
  } else {
    // neither is classic loaded nor has it been requested yet
    // --> make now promise to load ExtJS classic:
    const baseUrl = '/o/WebResourceProviderHook/extJS/sense-paintedwolf/';

    let jsUrl = baseUrl;
    let cssUrl1 = baseUrl + 'resources/';
    let cssUrl2 = cssUrl1;
    if (SenseConstants?.isDebug) {
      jsUrl += 'bundle-testing.js';
      cssUrl1 += 'bundle-testing_1.css';
      cssUrl2 += 'bundle-testing_2.css';
    } else {
      jsUrl += 'bundle-production.js';
      cssUrl1 += 'bundle-production_1.css';
      cssUrl2 += 'bundle-production_2.css';
    }

    loadCss(enrichWithVersion(cssUrl1));
    loadCss(enrichWithVersion(cssUrl2));
    stateExtJsClassic.loading = new Promise<void>(function (resolve, reject) {
      const enrichedJsUrl = enrichWithVersion(jsUrl);
      const loadSuccessHandler = () => {
        stateExtJsClassic.loaded = true;
        resolve();
      };
      // do not import ExtJS classic if already present
      if (document.querySelector(`script[src="${enrichedJsUrl}"]`)) {
        loadSuccessHandler();
      } else {
        loadJs(enrichedJsUrl)
          .then(loadSuccessHandler)
          .catch(function () {
            reject();
          });
      }
    });
    return stateExtJsClassic.loading;
  }
};

/**
 * Loads a certain classic app using the passed portlet namespace.
 * @param portletJSNamespace the portlet namespace.
 * @return a promise handling the CSS and Javascript File.
 */
export const loadClassicApp = function (
  portletJSNamespace: string,
  useResourceSubPath = false
): Promise<string[]> {
  let version = '' + new Date().getTime();
  for (let i = 0; i < document.scripts.length; i++) {
    const item = document.scripts[i];
    if (item && item.src && item.src.indexOf(portletJSNamespace) > -1) {
      const splitted = item.src.split('?');
      if (splitted && splitted.length === 2) {
        version = splitted[1];
        break;
      }
    }
  }

  const jsUrl =
    '/o/' + portletJSNamespace + '/extJS/sense-base/app.js?' + version;
  const cssUrl =
    '/o/' +
    portletJSNamespace +
    '/extJS/sense-base/' +
    (useResourceSubPath ? 'resources/' : '') +
    'app.css?' +
    version;

  return Promise.all([loadCss(cssUrl), loadJs(jsUrl)]);
};

/**
 * Initializes the classic app launcher.
 * @param appName the classic app name to launch
 * @param state the state of the portlet.
 * @param events the events upon the classic app should be launched
 * @param useResourceSubPath indicates wether the app contains the CSS sub path 'resources"
 */
export const loadInvokeClassicAppLauncher = function <Config>(
  appName: string,
  state: BasePortletStore<Config>,
  events: string[],
  useResourceSubPath: boolean,
  optionalID = ''
): void {
  // extract component instance:
  const cmpInstance = state.poeInstance;

  /**
   * Loads ExtJS, the appropriate application and performs a build Application on the POE.
   * @param eventName the first event, that triggers the awakening of the portlet.
   * @param event the event data itself.
   */
  async function handleFirstPoeEvent(
    eventName: string,
    event: unknown
  ): Promise<void> {
    // we are called the first time --> deregister handler as from now on the ExtJS listeners should do that
    events.forEach(e => cmpInstance.removeEventListener(e));

    try {
      // extract optional load mask:
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const setLoading = event.setLoading;

      // set loading:
      setLoading?.(true);

      await loadExtJsClassic();
      await loadClassicApp(appName, useResourceSubPath);

      setTimeout(() => {
        const newPoeInstance = getPOE()?.buildApplication(
          cmpInstance.componentID + optionalID,
          appName + '.Application',
          state,
          'ext-classic'
        );

        newPoeInstance
          ?.then(instance =>
            getPOE()?.fireEventOnSingleComponent(
              instance.componentID,
              eventName,
              event
            )
          )
          .finally(() => setLoading?.(false));
      }, 100);
    } catch (e) {
      console.error('Unable to load classic ' + appName + ' code!!!');
      showError(e);
    }
  }

  events.forEach(e => {
    // add event listeners:
    cmpInstance.addEventListener(
      e,
      (event: unknown) => {
        handleFirstPoeEvent(e, event);
      },
      null,
      null
    );
  });
};
